<script lang="tsx">

import {defineComponent, PropType} from "vue";

export const ButtonTypeKeys = ["Hero", "Primary", "Secondary", "Tertiary", "Text"] as const;
export type ButtonType = typeof ButtonTypeKeys[number];

export default defineComponent({
  name: "Button",
  props: {
    type: {
      type: String as PropType<ButtonType>,
      default: "Primary"
    },
    isActive: {
      type: Boolean,
      default: false
    },
    isIconOnly: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
  },
  setup(props, context) {
    return () => <button class={[
      'button',
      `button--${props.type}`,
      props.isActive ? 'button--active' : '',
      props.isDisabled ? 'button--disabled' : '',
      props.isIconOnly ? 'button--icon' : ''
    ]}>
      {context.slots.default?.()}
    </button>
  }
})
</script>


<style lang="scss" scoped>
@import "src/css/common/index";

@include important {
  .button {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    text-align: center;
    border: none;
    cursor: pointer;
    height: fit-content;

    &::v-deep(.icon) {
      font-size: 24px;
      margin-left: $spacing-1;
    }

    &--Hero {
      padding: $spacing-2 $spacing-7;
      background: $color-action-1;

      font-family: "SiemensSans", sans-serif;
      font-size: 20px;
      line-height: 28px;
      font-weight: 900;
      color: $color-surface;

      &::v-deep(.icon) {
        margin-right: -$spacing-3;
      }

      &:hover, &:active, &.button--active {
        background: $color-action-1-hover;
      }
    }

    &--Primary {
      padding: $spacing-1 $spacing-4;
      background: $color-action-2;

      font-family: "SiemensSans", sans-serif;
      font-size: 18px;
      line-height: 28px;
      font-weight: 900;
      color: $color-surface;

      &::v-deep(.icon) {
        margin-right: -$spacing-2;
      }

      &:hover, &:active, &.button--active {
        background: $color-action-3;
      }
    }

    &--Secondary {
      padding: $spacing-1 $spacing-4;
      border: 1px solid $color-action-2;
      background: none;

      font-family: "SiemensSans", sans-serif;
      font-size: 18px;
      line-height: 28px;
      font-weight: 900;
      color: $color-action-2;

      &::v-deep(.icon) {
        margin-right: -$spacing-2;
      }

      &:hover, &:active, &.button--active {
        background: $color-interaction-2;
        border-color: $color-action-3;
        color: $color-action-3;
      }
    }

    &--Tertiary {
      padding: $spacing-1 $spacing-4;
      background: none;

      font-family: "SiemensSans", sans-serif;
      font-size: 18px;
      line-height: 28px;
      font-weight: 900;
      color: $color-action-2;

      &::v-deep(.icon) {
        margin-right: -$spacing-2;
      }

      &:hover, &:active, &.button--active {
        background: $color-interaction-1;
      }
    }

    &--icon {
      padding: 10px;

      &::v-deep(.icon) {
        margin: 0;
      }
    }

    &--Text {
      font-family: "SiemensSans", sans-serif;
      font-style: normal;
      font-weight: 900;
      font-size: 18px;
      line-height: 28px;
      cursor: pointer;

      color: $color-action-2;
      text-decoration: none;

      margin-bottom: 3px;

      &::v-deep(.icon) {
        margin: 0;
      }

      &:hover, &:active {
        color: $color-action-3;
      }
    }

    &--icon {
      padding: 10px;

      &::v-deep(.icon) {
        margin: 0;
      }
    }

    &--disabled {
      opacity: 0.4;
      pointer-events: none;
      cursor: default;
    }
  }
}
</style>

